import { type FC, useCallback } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { FormProvider, useForm } from 'react-hook-form'

import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import {
  funnelTrackingFormSchema,
  getInitialValues,
} from '@/dialogs/FunnelTrackingCreateDialog/validation.ts'
import type { DialogProps } from '@/dialogs/types'
import type { FunnelCheckCreate } from '@/modules/tracking/funnel/types.ts'
import { extractUsername } from '@/utils/socials.ts'

import { FunnelTrackingForm } from '../../modules/tracking/funnel/components/forms/FunnelTrackingForm.tsx'

interface Props extends DialogProps {
  creatorName: string | null
  onSubmit: (body: FunnelCheckCreate) => Promise<void>
}

export const FunnelTrackingCreateDialog: FC<Props> = ({
  creatorName,
  isOpen,
  onClose,
  onSubmit,
}) => {
  const form = useForm({
    defaultValues: getInitialValues(creatorName),
    resolver: zodResolver(funnelTrackingFormSchema),
  })

  const handleSubmit = useCallback(
    async (values: FunnelCheckCreate) => {
      const formattedEntryPointData = extractUsername(values.entryPointData)

      await onSubmit({
        ...values,
        entryPointData: formattedEntryPointData,
      })

      onClose()
    },
    [onClose, onSubmit],
  )

  return (
    <Dialog
      show={isOpen}
      onClose={onClose}
    >
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <Dialog.Header>
            <Dialog.Title>Create Funnel Check</Dialog.Title>
          </Dialog.Header>
          <Dialog.Description>
            <div className="py-5">
              <FunnelTrackingForm />
            </div>
          </Dialog.Description>

          <Dialog.ClosableFooter
            onClose={onClose}
            className="mt-6"
          >
            <Button
              variant="default"
              isLoading={form.formState.isSubmitting}
            >
              Create
            </Button>
          </Dialog.ClosableFooter>
        </form>
      </FormProvider>
    </Dialog>
  )
}
